import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { ArrowLeft, Calendar, Clock, ArrowRight, Calculator, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { getBlogPosts, BlogPost } from '../lib/supabase';
import { motion } from 'framer-motion';

const POSTS_PER_PAGE = 18;
export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const blogPosts = await getBlogPosts(); // Get all posts
        setPosts(blogPosts);
      } catch (error) {
        // Silently handle the error - don't show console errors for missing table
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = posts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Blog | Golden Touch Contracting</title>
        <meta name="description" content="Read the latest insights, tips, and updates from Golden Touch Contracting. Expert advice on home renovation, construction, and design." />
        <meta name="keywords" content="construction blog, renovation tips, home improvement advice, building insights" />
        <link rel="canonical" href="https://renovationwestchester.com/blog" />
      </Helmet>

      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Home</span>
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
              Our Blog
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              Insights, tips, and updates from our construction and renovation experts. 
              Stay informed about the latest trends and best practices in home improvement.
            </p>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-zinc-400 text-lg">No blog posts available at the moment.</p>
              <p className="text-zinc-500 mt-2">Check back soon for new content!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-zinc-800/30 rounded-xl overflow-hidden hover:bg-zinc-700/30 transition-all duration-300 group"
                >
                  {post.image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-zinc-400 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(post.published_at || post.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{getReadingTime(post.content)}</span>
                      </div>
                    </div>
                    
                    <h2 className="text-xl font-light text-zinc-100 mb-3 group-hover:text-yellow-400 transition-colors">
                      {post.title}
                    </h2>
                    
                    <p className="text-zinc-400 mb-4 line-clamp-3">
                      {post.description}
                    </p>
                    
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-yellow-400 hover:gap-4 transition-all duration-300 group-hover:text-yellow-300"
                    >
                      <span className="text-sm tracking-wider">Read More</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-16 flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                const showPage = 
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1);
                
                const showEllipsis = 
                  (page === currentPage - 2 && currentPage > 3) ||
                  (page === currentPage + 2 && currentPage < totalPages - 2);

                if (showEllipsis) {
                  return (
                    <span key={page} className="px-2 text-zinc-500">
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-yellow-400 text-zinc-900'
                        : 'text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800/50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 bg-gradient-to-r from-zinc-800/50 via-zinc-800/30 to-zinc-800/50 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 via-transparent to-yellow-400/5 opacity-50"></div>
          <div className="absolute top-4 right-4 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-4 left-4 w-24 h-24 bg-yellow-400/10 rounded-full blur-2xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl font-light text-zinc-100 mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto leading-relaxed">
              Get an instant AI-powered estimate for your renovation project in under 1 minute, 
              or speak directly with our experts for personalized guidance.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <button
                onClick={() => {
                  const aiButton = document.querySelector('.fixed.bottom-4') as HTMLButtonElement;
                  if (aiButton) aiButton.click();
                }}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <Calculator className="h-5 w-5 relative z-10" />
                <div className="relative z-10">
                  <div className="text-sm font-medium">Get Online Estimate</div>
                  <div className="text-xs opacity-80">Takes less than 1 minute</div>
                </div>
              </button>
              
              <Link
                to="/consultation"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-zinc-800/50 backdrop-blur-sm border border-yellow-400/30 text-yellow-400 rounded-xl hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                <Phone className="h-5 w-5 relative z-10" />
                <div className="relative z-10">
                  <div className="text-sm font-medium">Free Consultation</div>
                  <div className="text-xs opacity-80">Speak with an expert</div>
                </div>
              </Link>
            </div>
            
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-zinc-500">
              <span>Or call us directly:</span>
              <a 
                href="tel:9149606433" 
                className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
              >
                (914) 960-6433
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}